// The CD thumbnail-drag contract, in a component-free module (react-refresh keeps CoreCards
// component-only). A thumbnail drag swaps the IMAGES between two slots; slot-bound fields
// (placement, antenna roles, photoId) deliberately STAY with the slot (CD: "placements and Antenna
// roles stay with the slot").

/** The DataTransfer type marking an in-page thumbnail drag (vs an OS file drag). */
export const PHOTO_DRAG_TYPE = "application/x-cntrl-photo-slot";

export interface PhotoDraft {
  photoId: string | null;
  slot: number;
  mediaId: string;
  url: string | null;
  thumbUrl: string | null;
  contentType: string | null;
  width: number | null;
  height: number | null;
  epkRole: string | null;
  caption: string | null;
  isAntennaAvatar: boolean;
  isAntennaCover: boolean;
}

type MediaFields = Pick<PhotoDraft, "mediaId" | "url" | "thumbUrl" | "contentType" | "width" | "height">;

const takeMedia = (d: PhotoDraft): MediaFields => ({
  mediaId: d.mediaId, url: d.url, thumbUrl: d.thumbUrl,
  contentType: d.contentType, width: d.width, height: d.height,
});

/** Swap the IMAGES between two slots. Swapping into an empty slot is a move: the source row
 * empties (and disappears — a draft without media can't persist). */
export function swapPhotoMedia(drafts: PhotoDraft[], fromSlot: number, toSlot: number): PhotoDraft[] {
  if (fromSlot === toSlot) return drafts;
  const from = drafts.find((d) => d.slot === fromSlot);
  if (!from) return drafts;
  const to = drafts.find((d) => d.slot === toSlot);
  if (to) {
    const fromMedia = takeMedia(from);
    const toMedia = takeMedia(to);
    return drafts.map((d) =>
      d.slot === fromSlot ? { ...d, ...toMedia } : d.slot === toSlot ? { ...d, ...fromMedia } : d,
    );
  }
  // Move into the empty slot: target gets the media (slot-fresh placement/antenna), source empties out.
  const moved: PhotoDraft = {
    photoId: null, slot: toSlot, ...takeMedia(from),
    epkRole: null, caption: null, isAntennaAvatar: false, isAntennaCover: false,
  };
  return [...drafts.filter((d) => d.slot !== fromSlot), moved].sort((a, b) => a.slot - b.slot);
}
